// frontend/src/components/Tickets/TicketsDashboard.tsx
// Раздел «Тикеты» — кросс-чат очередь нативной тикет-системы mbbot.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';

interface TicketChat {
  id: number;
  full_name?: string;
  display_name?: string;
  company_name?: string;
  license?: string;
  group_id?: number;
  group_name?: string;
  last_message_at?: string;
  is_active?: boolean;
}

interface Ticket {
  id: number;
  chat_id: number;
  number: number;
  status: 'open' | 'in_work' | 'resolved';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  subject?: string;
  category?: string | null;
  categories?: string[];           // Phase B: все категории обращения (мультивыбор)
  category_source?: string | null;
  assigned_to?: number;
  assigned_to_name?: string;
  opened_at?: string;
  sla_due_at?: string;
  sla_state?: 'ok' | 'warning' | 'breached';
  chat?: TicketChat;
}

interface Operator {
  id: number;
  login: string;
  pseudonym?: string;
  full_name?: string;
}

interface Stats {
  by_status: Record<string, number>;
  sla_breached: number;
  total: number;
  by_operator: Array<{ user_id: number; name: string; open_tickets: number }>;
}

interface Analytics {
  days: number;
  total: number;
  by_base: Record<string, number>;
  by_category: Record<string, number>;
  by_priority: Record<string, number>;
  by_source: Record<string, number>;
  sla_breached: number;
  avg_first_response_min: number | null;
  responded: number;
}

const BASE_LABEL: Record<string, string> = {
  bug: '🐛 Баги', feature: '🛠 Доработки', install: '📦 Установка/перенос',
  help: '🆘 Вопросы', uncategorized: '◾ Без категории',
};
const BASE_COLOR: Record<string, string> = {
  bug: '#dc2626', feature: '#7c3aed', install: '#0891b2', help: '#2563eb', uncategorized: '#9ca3af',
};
const SOURCE_LABEL: Record<string, string> = {
  bot: 'кнопка', ai: 'AI', operator: 'оператор', ai_none: 'AI: не определил', none: '—',
};
// Компактные чипы категорий обращения (мультикатегории Phase B)
const CHIP_EMOJI: Record<string, string> = { bug: '🐛', feature: '🛠', install: '📦', help: '🆘' };
const ticketCategoryList = (t: { categories?: string[]; category?: string | null }): string[] => {
  const raw = (t.categories && t.categories.length > 0)
    ? t.categories
    : (t.category ? [t.category] : []);
  const seen = new Set<string>();
  const out: string[] = [];
  for (const c of raw) {
    const base = String(c).split(':')[0];
    if (CHIP_EMOJI[base] && !seen.has(c)) { seen.add(c); out.push(c); }
  }
  return out;
};

const fmtDuration = (mins: number | null) => {
  if (mins == null) return '—';
  if (mins < 60) return `${Math.round(mins)} мин`;
  const h = Math.floor(mins / 60); const m = Math.round(mins % 60);
  return m ? `${h} ч ${m} мин` : `${h} ч`;
};

const STATUS_LABEL: Record<string, string> = { open: 'Открыт', in_work: 'В работе', resolved: 'Решён' };
const STATUS_COLOR: Record<string, string> = { open: '#2563eb', in_work: '#16a34a', resolved: '#6b7280' };
const PRIORITY_LABEL: Record<string, string> = { low: 'Низкий', normal: 'Обычный', high: 'Высокий', urgent: 'Срочный' };
const PRIORITY_COLOR: Record<string, string> = { low: '#9ca3af', normal: '#3b82f6', high: '#f59e0b', urgent: '#dc2626' };
const SLA_COLOR: Record<string, string> = { ok: '#16a34a', warning: '#f59e0b', breached: '#dc2626' };

const fmtDate = (iso?: string) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso.replace('Z', ''));
    return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch { return '—'; }
};

const operatorName = (o: Operator) => o.pseudonym || o.full_name || o.login;

export const TicketsDashboard: React.FC = () => {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [anDays, setAnDays] = useState<number>(90);
  const [operators, setOperators] = useState<Operator[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<'' | 'open' | 'in_work' | 'resolved'>('open');
  const [priorityFilter, setPriorityFilter] = useState<string>('');
  const [assignedFilter, setAssignedFilter] = useState<string>('');
  const [q, setQ] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (statusFilter) params.status = statusFilter;
      if (priorityFilter) params.priority = priorityFilter;
      if (assignedFilter) params.assigned_to = assignedFilter;
      if (q.trim()) params.q = q.trim();
      const [tRes, sRes, aRes] = await Promise.all([
        api.get('/api/tickets', { params }),
        api.get('/api/tickets/stats'),
        api.get('/api/tickets/analytics', { params: { days: anDays } }),
      ]);
      setTickets(tRes.data?.items || []);
      setStats(sRes.data || null);
      setAnalytics(aRes.data || null);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to load tickets', e);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, priorityFilter, assignedFilter, q, anDays]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get('/api/users/operators')
      .then(r => setOperators(Array.isArray(r.data) ? r.data : (r.data?.data || [])))
      .catch(() => setOperators([]));
  }, []);

  const patch = async (fn: () => Promise<any>) => {
    try { await fn(); await load(); } catch (e) { /* eslint-disable-line */ console.error(e); }
  };

  const changeStatus = (t: Ticket, status: string) =>
    patch(() => api.post(`/api/tickets/${t.id}/status`, { status }));
  const changePriority = (t: Ticket, priority: string) =>
    patch(() => api.post(`/api/tickets/${t.id}/priority`, { priority }));
  const changeAssignee = (t: Ticket, user_id: string) =>
    patch(() => api.post(`/api/tickets/${t.id}/assign`, { user_id: user_id ? Number(user_id) : null }));

  const openChat = (t: Ticket) => {
    window.location.href = `/dashboard?chat=${t.chat_id}`;
  };

  return (
    <div style={{ padding: '1rem 1.5rem', maxWidth: 1280, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <button onClick={() => { window.location.href = '/dashboard'; }}
                style={{ border: '1px solid #d1d5db', background: '#fff', borderRadius: 8, padding: '6px 12px', cursor: 'pointer' }}>
          <i className="bi bi-arrow-left"></i> Назад
        </button>
        <h2 style={{ margin: 0 }}>🎫 Тикеты</h2>
        <button onClick={load} style={{ marginLeft: 'auto', border: '1px solid #d1d5db', background: '#fff', borderRadius: 8, padding: '6px 12px', cursor: 'pointer' }}>
          <i className="bi bi-arrow-clockwise"></i> Обновить
        </button>
      </div>

      {/* Сводка */}
      {stats && (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
          {(['open', 'in_work', 'resolved'] as const).map(s => (
            <div key={s} style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: '10px 16px', minWidth: 120 }}>
              <div style={{ fontSize: 12, color: '#6b7280' }}>{STATUS_LABEL[s]}</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: STATUS_COLOR[s] }}>{stats.by_status[s] || 0}</div>
            </div>
          ))}
          <div style={{ border: '1px solid #fecaca', background: '#fef2f2', borderRadius: 10, padding: '10px 16px', minWidth: 120 }}>
            <div style={{ fontSize: 12, color: '#b91c1c' }}>SLA просрочено</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#dc2626' }}>{stats.sla_breached}</div>
          </div>
        </div>
      )}

      {/* Аналитика обращений за период */}
      {analytics && (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, marginBottom: 16, background: '#fafafa' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
            <strong style={{ fontSize: 14 }}>📊 Аналитика обращений</strong>
            <span style={{ fontSize: 12, color: '#6b7280' }}>за период:</span>
            {[7, 30, 90, 365].map(d => (
              <button key={d} onClick={() => setAnDays(d)}
                style={{ border: '1px solid', borderColor: anDays === d ? '#2563eb' : '#d1d5db',
                         background: anDays === d ? '#2563eb' : '#fff', color: anDays === d ? '#fff' : '#374151',
                         borderRadius: 14, padding: '2px 10px', fontSize: 12, cursor: 'pointer' }}>
                {d === 7 ? 'неделя' : d === 30 ? 'месяц' : d === 90 ? '3 мес' : 'год'}
              </button>
            ))}
            <span style={{ marginLeft: 'auto', fontSize: 12, color: '#6b7280' }}>
              всего: <b>{analytics.total}</b> · ⏱ ср. реакция: <b>{fmtDuration(analytics.avg_first_response_min)}</b>
              {analytics.responded ? <span style={{ color: '#9ca3af' }}> ({analytics.responded})</span> : null}
            </span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
            {/* По категориям */}
            <div>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>По категориям</div>
              {Object.entries(analytics.by_base).sort((a, b) => b[1] - a[1]).map(([base, cnt]) => {
                const pct = analytics.total ? Math.round((cnt / analytics.total) * 100) : 0;
                return (
                  <div key={base} style={{ marginBottom: 6 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 2 }}>
                      <span>{BASE_LABEL[base] || base}</span>
                      <span style={{ color: '#6b7280' }}>{cnt} · {pct}%</span>
                    </div>
                    <div style={{ height: 6, background: '#eef0f2', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: BASE_COLOR[base] || '#9ca3af' }} />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Топ под-тегов */}
            <div>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>Топ тем (с под-тегом)</div>
              {Object.entries(analytics.by_category)
                .filter(([c]) => c.includes(':'))
                .slice(0, 8)
                .map(([cat, cnt]) => (
                  <div key={cat} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '2px 0' }}>
                    <span>{cat}</span><span style={{ color: '#6b7280' }}>{cnt}</span>
                  </div>
                ))}
              {!Object.keys(analytics.by_category).some(c => c.includes(':')) && (
                <div style={{ fontSize: 12, color: '#9ca3af' }}>нет под-тегов</div>
              )}
            </div>

            {/* По приоритету + источник */}
            <div>
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>По приоритету</div>
              {(['urgent', 'high', 'normal', 'low'] as const).map(p => (
                analytics.by_priority[p] ? (
                  <div key={p} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '2px 0' }}>
                    <span style={{ color: PRIORITY_COLOR[p] }}>{PRIORITY_LABEL[p]}</span>
                    <span style={{ color: '#6b7280' }}>{analytics.by_priority[p]}</span>
                  </div>
                ) : null
              ))}
              <div style={{ fontSize: 12, color: '#6b7280', margin: '8px 0 6px' }}>Источник категории</div>
              {Object.entries(analytics.by_source).sort((a, b) => b[1] - a[1]).map(([src, cnt]) => (
                <div key={src} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '2px 0' }}>
                  <span>{SOURCE_LABEL[src] || src}</span><span style={{ color: '#6b7280' }}>{cnt}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Фильтры */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
        {([['open', 'Открытые'], ['in_work', 'В работе'], ['resolved', 'Решённые'], ['', 'Все']] as const).map(([val, label]) => (
          <button key={val} onClick={() => setStatusFilter(val as any)}
                  style={{ border: '1px solid #d1d5db', background: statusFilter === val ? '#111827' : '#fff', color: statusFilter === val ? '#fff' : '#111827', borderRadius: 999, padding: '5px 14px', cursor: 'pointer', fontSize: 13 }}>
            {label}
          </button>
        ))}
        <select value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)}
                style={{ border: '1px solid #d1d5db', borderRadius: 8, padding: '6px 10px' }}>
          <option value="">Любой приоритет</option>
          {Object.keys(PRIORITY_LABEL).map(p => <option key={p} value={p}>{PRIORITY_LABEL[p]}</option>)}
        </select>
        <select value={assignedFilter} onChange={e => setAssignedFilter(e.target.value)}
                style={{ border: '1px solid #d1d5db', borderRadius: 8, padding: '6px 10px' }}>
          <option value="">Любой оператор</option>
          {operators.map(o => <option key={o.id} value={o.id}>{operatorName(o)}</option>)}
        </select>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Поиск (компания, лицензия, тема)…"
               style={{ border: '1px solid #d1d5db', borderRadius: 8, padding: '6px 10px', minWidth: 240, flex: 1 }} />
      </div>

      {/* Список */}
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Загрузка…</div>
      ) : tickets.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Тикетов не найдено</div>
      ) : (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#f9fafb', textAlign: 'left' }}>
                <th style={{ padding: '10px 12px' }}>#</th>
                <th style={{ padding: '10px 12px' }}>Статус</th>
                <th style={{ padding: '10px 12px' }}>Приоритет</th>
                <th style={{ padding: '10px 12px' }}>Категории</th>
                <th style={{ padding: '10px 12px' }}>Компания / лицензия</th>
                <th style={{ padding: '10px 12px' }}>Оператор</th>
                <th style={{ padding: '10px 12px' }}>Открыт</th>
                <th style={{ padding: '10px 12px' }}>SLA</th>
                <th style={{ padding: '10px 12px' }}></th>
              </tr>
            </thead>
            <tbody>
              {tickets.map(t => (
                <tr key={t.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '8px 12px', fontWeight: 600 }}>
                    #{t.id}
                    {t.number > 1 && (
                      <div title={`${t.number}-е обращение клиента`} style={{ fontSize: 11, color: '#9ca3af' }}>
                        🔁 №{t.number}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <select value={t.status} onChange={e => changeStatus(t, e.target.value)}
                            style={{ border: `1px solid ${STATUS_COLOR[t.status]}`, color: STATUS_COLOR[t.status], borderRadius: 6, padding: '3px 6px', background: '#fff' }}>
                      <option value="open">Открыт</option>
                      <option value="in_work">В работе</option>
                      <option value="resolved">Решён</option>
                    </select>
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <select value={t.priority} onChange={e => changePriority(t, e.target.value)}
                            style={{ border: `1px solid ${PRIORITY_COLOR[t.priority]}`, color: PRIORITY_COLOR[t.priority], borderRadius: 6, padding: '3px 6px', background: '#fff' }}>
                      {Object.keys(PRIORITY_LABEL).map(p => <option key={p} value={p}>{PRIORITY_LABEL[p]}</option>)}
                    </select>
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {ticketCategoryList(t).map(cat => {
                        const base = cat.split(':')[0];
                        const sub = cat.split(':')[1];
                        return (
                          <span key={cat} title={cat}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 3, background: BASE_COLOR[base] || '#9ca3af', color: '#fff', borderRadius: 10, padding: '1px 7px', fontSize: 12, whiteSpace: 'nowrap' }}>
                            <span>{CHIP_EMOJI[base]}</span>
                            {sub && <span style={{ fontSize: 11, opacity: 0.95 }}>{sub}</span>}
                          </span>
                        );
                      })}
                      {ticketCategoryList(t).length === 0 && <span style={{ fontSize: 12, color: '#9ca3af' }}>—</span>}
                    </div>
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <div style={{ fontWeight: 500 }}>{t.chat?.company_name || t.chat?.full_name || `Чат ${t.chat_id}`}</div>
                    {t.chat?.license && <div style={{ fontSize: 12, color: '#6b7280' }}>лиц. {t.chat.license}</div>}
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <select value={t.assigned_to || ''} onChange={e => changeAssignee(t, e.target.value)}
                            style={{ border: '1px solid #d1d5db', borderRadius: 6, padding: '3px 6px', background: '#fff', maxWidth: 150 }}>
                      <option value="">— не назначен —</option>
                      {operators.map(o => <option key={o.id} value={o.id}>{operatorName(o)}</option>)}
                    </select>
                  </td>
                  <td style={{ padding: '8px 12px', color: '#6b7280', whiteSpace: 'nowrap' }}>{fmtDate(t.opened_at)}</td>
                  <td style={{ padding: '8px 12px' }}>
                    <span title={t.sla_state} style={{ display: 'inline-block', width: 12, height: 12, borderRadius: '50%', background: SLA_COLOR[t.sla_state || 'ok'] }}></span>
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <button onClick={() => openChat(t)} style={{ border: '1px solid #d1d5db', background: '#fff', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                      Открыть чат <i className="bi bi-box-arrow-up-right"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TicketsDashboard;
